import { apiClient, handleApiError, handleApiResponse } from './api'

export interface TeamMember {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  confirmed: boolean
  lastSignInAt?: string
  onboardingCompletedAt?: string
  status: 'active'
  type: 'user'
}

export interface PendingInvitation {
  id: string
  email: string
  status: 'pending' | 'expired'
  expiresAt: string
  createdAt: string
  invitedBy: {
    id: string
    fullName: string
    email: string
  }
  expired: boolean
  userExists: boolean
  emailTemplateType: string
  type: 'invitation'
}

export type TeamMemberOrInvitation = TeamMember | PendingInvitation

export interface InviteUserRequest {
  email: string
}

export interface InviteUserResponse {
  id: string
  email: string
  status: string
  expiresAt: string
  createdAt: string
  invitedBy: TeamMember
  organization: {
    id: string
    name: string
  }
}

class InvitationService {
  private getCurrentOrganizationId(): string | null {
    // Get organization ID from auth store
    const authData = localStorage.getItem('auth-storage')
    if (authData) {
      try {
        const parsed = JSON.parse(authData)
        return parsed.state?.organization?.id || null
      } catch (error) {
        console.warn('Error reading organization ID from storage', error)
      }
    }
    return null
  }

  /**
   * Get all team members and pending invitations for current organization
   */
  async getTeamMembersAndInvitations(): Promise<TeamMemberOrInvitation[]> {
    try {
      const organizationId = this.getCurrentOrganizationId()

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      // Fetch both users and invitations in parallel
      const [usersResponse, invitationsResponse] = await Promise.all([
        apiClient.get(`/organizations/${organizationId}/users`),
        apiClient.get(`/organizations/${organizationId}/user_invitations`),
      ])

      const usersData = handleApiResponse<TeamMember[]>(usersResponse)
      const invitationsData =
        handleApiResponse<PendingInvitation[]>(invitationsResponse)

      const users: TeamMember[] = (usersData || []).map((user: TeamMember) => ({
        ...user,
        status: 'active' as const,
        type: 'user' as const,
      }))

      const invitations: PendingInvitation[] = (invitationsData || []).map(
        (invitation: PendingInvitation) => ({
          ...invitation,
          type: 'invitation' as const,
        })
      )

      return [...users, ...invitations]
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Send invitation to user
   */
  async inviteUser(email: string): Promise<InviteUserResponse> {
    try {
      const organizationId = this.getCurrentOrganizationId()

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      const response = await apiClient.post(
        `/organizations/${organizationId}/user_invitations`,
        {
          user_invitation: { email },
        }
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Cancel/revoke an invitation
   */
  async revokeInvitation(invitationId: string): Promise<void> {
    try {
      const organizationId = this.getCurrentOrganizationId()

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      await apiClient.delete(
        `/organizations/${organizationId}/user_invitations/${invitationId}`
      )
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Resend an invitation
   */
  async resendInvitation(invitationId: string): Promise<PendingInvitation> {
    try {
      const organizationId = this.getCurrentOrganizationId()

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      const response = await apiClient.post(
        `/organizations/${organizationId}/user_invitations/${invitationId}/resend`
      )

      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Remove user from organization (God Mode only)
   */
  async removeUser(userId: string): Promise<void> {
    try {
      const organizationId = this.getCurrentOrganizationId()

      if (!organizationId) {
        throw new Error('No organization selected')
      }

      await apiClient.delete(`/organizations/${organizationId}/users/${userId}`)
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Accept an invitation using token (public endpoint)
   */
  async acceptInvitation(token: string): Promise<{
    signup_required?: boolean
    organization?: {
      id: string
      name: string
    }
  }> {
    try {
      const response = await apiClient.post(`/invitations/accept/${token}`)
      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }

  /**
   * Get invitation details by token (public endpoint)
   */
  async getInvitationDetails(token: string): Promise<{
    status: string
    valid: boolean
    email: string
    organizationName: string
    inviterName: string
    expiresAt: string
  }> {
    try {
      const response = await apiClient.get(`/invitations/details/${token}`)
      return handleApiResponse(response)
    } catch (error) {
      const apiError = handleApiError(error)
      throw new Error(apiError.message)
    }
  }
}

export const invitationService = new InvitationService()
